// ═══════════════════════════════════════════════════════════════════════════
// PROJECTS — single source of truth for the work showcase.
//
// Consumed by:
//   • pages/ProjectsPage.jsx        — index / filterable showcase
//   • pages/ProjectDetailPage.jsx   — full case-study page
//   • components/Nav.jsx, Footer.jsx (links only)
//
// Sector slugs + colors intentionally mirror data/sectorServicesData.js so a
// project card, a sector page, and the nav all speak the same visual language.
//
// NOTE FOR CONTENT OWNERS: figures below are illustrative placeholders modeled
// on the shape of Zone-X engagements. Replace `metrics`, `client`, and `quote`
// with signed-off numbers before this page goes public.
// ═══════════════════════════════════════════════════════════════════════════

export const PROJECT_SECTORS = [
  { slug: 'all',              name: 'All Work',           color: '#0A0A14' },
  { slug: 'healthcare',       name: 'Healthcare',         color: '#C0392B' },
  { slug: 'utilities-energy', name: 'Utilities & Energy', color: '#E67E22' },
  { slug: 'industrial-iot',   name: 'Industrial IoT',     color: '#5D4037' },
  { slug: 'education',        name: 'Education',          color: '#1565A7' },
];

export const PROJECTS = [
  // ───────────────────────────────────────────────────────── HEALTHCARE ──
  {
    slug: 'neonatal-command-layer',
    code: 'ZX-24-01',
    featured: true,
    title: 'Neonatal Command Layer',
    tagline: 'One screen for every incubator in the unit',
    sector: 'healthcare',
    sectorName: 'Healthcare',
    color: '#C0392B',
    year: '2024',
    duration: '9 months',
    team: '7 engineers',
    client: 'Multi-site paediatric hospital group',
    clientNote: '3 hospitals · 62 NICU beds',
    services: ['Embedded Hardware', 'Firmware', 'Cloud Platform', 'Clinical UX'],
    image: '/visuals/sector-services/healthcare/1.png',
    gallery: [
      '/visuals/sector-services/healthcare/1.png',
      '/visuals/sector-services/healthcare/2.png',
      '/visuals/sector-services/healthcare/5.png',
    ],
    tags: ['BLE', 'HL7 FHIR', 'Edge AI', 'React'],
    summary:
      'A bedside-to-command-centre telemetry layer that unifies warmers, phototherapy units, ventilators and vitals monitors from six different OEMs into one live view of the neonatal unit.',
    challenge: {
      body:
        'Nurses were reading vitals off six vendor screens and copying them onto paper charts once an hour. Deterioration between chart entries was invisible, and no two hospitals in the group recorded the same fields.',
      points: [
        'Six device vendors, four incompatible serial and proprietary BLE protocols',
        'No device could be modified — every unit had to stay within its OEM warranty',
        'Alarm fatigue: 380+ audible alerts per shift, roughly 70% clinically irrelevant',
        'Hospital IT required full network isolation from the clinical VLAN',
      ],
    },
    approach: {
      body:
        'We built a non-invasive capture layer rather than replacing a single machine. A small certified gateway sits behind each bed, speaks each vendor dialect, and normalises everything into FHIR before it ever leaves the ward.',
      steps: [
        { title: 'Protocol archaeology', body: 'Two weeks on-site capturing and decoding every device’s serial and BLE output, producing a signed protocol matrix for all six OEMs.' },
        { title: 'Bedside gateway', body: 'A fanless ARM gateway with opto-isolated serial, dual-band BLE and a 4-hour supercap buffer so a power blip never loses a reading.' },
        { title: 'Alarm intelligence', body: 'An on-gateway model correlates cross-device context before escalating — a SpO₂ dip during a documented handling event no longer fires the ward alarm.' },
        { title: 'Clinical surface', body: 'A single ward wallboard plus a phone view for the on-call consultant, designed with the nursing leads across four iterations.' },
      ],
    },
    outcome: {
      body:
        'The unit moved from hourly manual charting to continuous automatic capture, and the alarm environment became quiet enough that alerts regained meaning.',
      points: [
        'Charting time returned to bedside care: roughly 52 nurse-minutes per shift',
        'Every reading now audit-traceable for NABH review with no extra paperwork',
        'Rolled out to two further hospitals in the group from the same codebase',
      ],
    },
    metrics: [
      { value: '71%',    label: 'Fewer Alarm Events', sub: 'clinically filtered' },
      { value: '<800ms', label: 'Bed to Wallboard',   sub: 'end-to-end latency' },
      { value: '62',     label: 'Beds Live',          sub: 'across 3 sites' },
      { value: '99.97%', label: 'Gateway Uptime',     sub: 'rolling 12 months' },
    ],
    stack: [
      { group: 'Edge',     items: ['C / C++', 'FreeRTOS', 'ARM Cortex-M7', 'BLE 5.2', 'Modbus RTU'] },
      { group: 'Platform', items: ['Node.js', 'MQTT', 'TimescaleDB', 'HL7 FHIR', 'Docker'] },
      { group: 'Surface',  items: ['React', 'WebSocket', 'D3', 'PWA'] },
    ],
    architecture: [
      { layer: 'EDGE',     title: 'Bedside capture',    nodes: ['Warmer', 'Phototherapy', 'Vitals monitor', 'Ventilator'] },
      { layer: 'GATEWAY',  title: 'Normalise & buffer', nodes: ['Protocol adapters', 'Alarm correlation', 'Store-and-forward'] },
      { layer: 'PLATFORM', title: 'Stream & store',     nodes: ['MQTT broker', 'FHIR mapper', 'Time-series store'] },
      { layer: 'SURFACE',  title: 'Clinical view',      nodes: ['Ward wallboard', 'Consultant mobile', 'NABH export'] },
    ],
    timeline: [
      { phase: 'Discovery', weeks: 'Weeks 1–4',   body: 'Ward shadowing, protocol capture, clinical requirement sign-off.' },
      { phase: 'Prototype', weeks: 'Weeks 5–12',  body: 'Gateway board bring-up, two-bed pilot running beside paper charts.' },
      { phase: 'Hardening', weeks: 'Weeks 13–26', body: 'EMC testing, failover, alarm model tuning against 9 weeks of ward data.' },
      { phase: 'Rollout',   weeks: 'Weeks 27–36', body: 'Full unit deployment, nurse training, handover of runbooks and source.' },
    ],
    quote: {
      text: 'The screen stopped being another thing to check and became the thing we check. That is the whole difference.',
      author: 'Nursing Superintendent',
      role: 'Lead NICU, client hospital group',
    },
  },

  {
    slug: 'phototherapy-fleet-telemetry',
    code: 'ZX-23-07',
    featured: false,
    title: 'Phototherapy Fleet Telemetry',
    tagline: 'Every blue-light minute, measured and logged',
    sector: 'healthcare',
    sectorName: 'Healthcare',
    color: '#C0392B',
    year: '2023',
    duration: '5 months',
    team: '4 engineers',
    client: 'Medical device manufacturer',
    clientNote: 'OEM retrofit programme',
    services: ['Embedded Hardware', 'Firmware', 'Cloud Integration'],
    image: '/visuals/sector-services/healthcare/2.png',
    gallery: ['/visuals/sector-services/healthcare/2.png', '/visuals/sector-services/healthcare/4.png'],
    tags: ['Irradiance', 'LoRa', 'OTA', 'Audit'],
    summary:
      'A retrofit sensing and telemetry module that turns an installed base of manual phototherapy units into a self-documenting, irradiance-verified fleet.',
    challenge: {
      body:
        'Treatment dose was being estimated from a wall clock and a lamp-hours counter. Nobody could prove what irradiance a given baby actually received, and lamp degradation went unnoticed until a nurse thought the light "looked dim".',
      points: [
        'No irradiance measurement anywhere in the installed fleet',
        'Retrofit module had to fit an existing enclosure with no tooling change',
        'Hospitals had no reliable Wi-Fi in the wards where the units live',
      ],
    },
    approach: {
      body:
        'A calibrated photodiode head plus a distance sensor gives real dose, not estimated dose. LoRa carries it out of the ward without touching hospital IT.',
      steps: [
        { title: 'Sensor head', body: 'A 450nm-selective photodiode with a per-unit factory calibration curve stored in the module’s secure element.' },
        { title: 'Dose engine', body: 'Firmware integrates irradiance × time × exposed area to produce a true delivered dose per treatment session.' },
        { title: 'Ward-independent link', body: 'Sub-GHz LoRa to a single building gateway — no ward Wi-Fi, no hospital IT ticket, no VLAN negotiation.' },
        { title: 'Fleet console', body: 'Lamp-degradation trending across the estate so units are serviced on measured output, not on a calendar.' },
      ],
    },
    outcome: {
      body:
        'The manufacturer shipped the module as a paid upgrade across its installed base and now sells the fleet console as a recurring service line.',
      points: [
        'Dose records became NABH-audit ready with zero nursing input',
        'Lamp replacement moved from fixed-interval to measured-degradation',
        'Module retrofits in under 20 minutes by a field technician',
      ],
    },
    metrics: [
      { value: '±3%',   label: 'Dose Accuracy',     sub: 'vs lab reference' },
      { value: '340+',  label: 'Units Retrofitted', sub: 'first 12 months' },
      { value: '28%',   label: 'Lamp Spend Cut',    sub: 'condition-based swap' },
      { value: '20min', label: 'Retrofit Time',     sub: 'per unit, in field' },
    ],
    stack: [
      { group: 'Edge',     items: ['C', 'STM32L4', 'LoRa SX1262', 'I²C sensor bus'] },
      { group: 'Platform', items: ['Python', 'FastAPI', 'PostgreSQL', 'AWS IoT Core'] },
      { group: 'Surface',  items: ['React', 'Recharts'] },
    ],
    architecture: [
      { layer: 'EDGE',     title: 'Sensing module', nodes: ['Photodiode head', 'Distance sensor', 'Dose integrator'] },
      { layer: 'GATEWAY',  title: 'Building link',  nodes: ['LoRa concentrator', 'Store-and-forward'] },
      { layer: 'PLATFORM', title: 'Fleet backend',  nodes: ['Ingest API', 'Calibration registry', 'OTA service'] },
      { layer: 'SURFACE',  title: 'Fleet console',  nodes: ['Degradation trends', 'Audit export', 'Service scheduling'] },
    ],
    timeline: [
      { phase: 'Feasibility', weeks: 'Weeks 1–3',   body: 'Optical bench work to prove a low-cost head could hit clinical accuracy.' },
      { phase: 'Design',      weeks: 'Weeks 4–10',  body: 'Module PCB, enclosure fit study, calibration jig design.' },
      { phase: 'Pilot',       weeks: 'Weeks 11–16', body: '25-unit pilot across two hospitals with reference-meter cross-checks.' },
      { phase: 'Production',  weeks: 'Weeks 17–22', body: 'DFM handover, factory calibration line, field-technician training.' },
    ],
    quote: null,
  },

  {
    slug: 'remote-patient-monitoring-platform',
    code: 'ZX-24-05',
    featured: false,
    title: 'Remote Patient Monitoring Platform',
    tagline: 'Cardiac follow-up that does not need a waiting room',
    sector: 'healthcare',
    sectorName: 'Healthcare',
    color: '#C0392B',
    year: '2024',
    duration: '7 months',
    team: '6 engineers',
    client: 'Cardiology group practice',
    clientNote: '2,400 enrolled patients',
    services: ['Cloud Platform', 'Mobile Apps', 'EMR Integration', 'Analytics'],
    image: '/visuals/sector-services/healthcare/3.png',
    gallery: ['/visuals/sector-services/healthcare/3.png', '/visuals/sector-services/healthcare/4.png'],
    tags: ['BLE', 'FHIR', 'React Native', 'Anomaly ML'],
    summary:
      'A post-discharge monitoring platform pairing consumer BLE devices with a clinician triage queue that surfaces the twelve patients who need attention today, not the two thousand who do not.',
    challenge: {
      body:
        'The practice piloted a monitoring app and drowned in it. Two thousand patients uploading daily vitals produced a dashboard nobody could triage, so within a month the data went unread.',
      points: [
        'Signal buried in volume — no prioritisation, just a chronological feed',
        'Patients aged 60+ abandoned the first app within two weeks',
        'Readings never reached the EMR, so consultations still started from zero',
      ],
    },
    approach: {
      body:
        'We inverted the model: the platform decides who the clinician sees. Everything else stays silent until a trend crosses a patient-specific band.',
      steps: [
        { title: 'Per-patient baselines', body: 'Each patient’s own 14-day baseline replaces population thresholds, so normal-for-them stops firing alerts.' },
        { title: 'Triage queue', body: 'The clinician surface is a ranked worklist, not a dashboard — with the reason for ranking stated on every row.' },
        { title: 'Assisted onboarding', body: 'Guided pairing with large type, voice prompts and a printed fallback card, tested with patients over 65.' },
        { title: 'EMR write-back', body: 'FHIR observations land in the existing EMR so the consultation opens with the data already there.' },
      ],
    },
    outcome: {
      body:
        'Clinician review time per patient collapsed because most patients no longer need review, and enrolment retention held past six months.',
      points: [
        'Median triage list: 12 patients per day out of 2,400 enrolled',
        '90-day patient retention held at 84% versus 31% in the earlier pilot',
        'Every reading available in the EMR before the patient arrives',
      ],
    },
    metrics: [
      { value: '2,400', label: 'Patients Enrolled',     sub: 'active programme' },
      { value: '84%',   label: '90-Day Retention',      sub: 'vs 31% baseline' },
      { value: '12',    label: 'Daily Triage List',     sub: 'down from 2,400' },
      { value: '6.5h',  label: 'Clinician Hours Saved', sub: 'per week' },
    ],
    stack: [
      { group: 'Mobile',    items: ['React Native', 'BLE GATT', 'Offline sync'] },
      { group: 'Platform',  items: ['Go', 'Kafka', 'PostgreSQL', 'HL7 FHIR', 'Kubernetes'] },
      { group: 'Analytics', items: ['Python', 'scikit-learn', 'Baseline models'] },
    ],
    architecture: [
      { layer: 'EDGE',     title: 'Home devices',   nodes: ['BP cuff', 'SpO₂', 'Weight scale', 'Single-lead ECG'] },
      { layer: 'GATEWAY',  title: 'Patient phone',  nodes: ['BLE pairing', 'Offline queue', 'Reminders'] },
      { layer: 'PLATFORM', title: 'Ingest & score', nodes: ['Event stream', 'Baseline engine', 'FHIR write-back'] },
      { layer: 'SURFACE',  title: 'Clinician',      nodes: ['Triage worklist', 'Trend view', 'EMR context'] },
    ],
    timeline: [
      { phase: 'Discovery',      weeks: 'Weeks 1–4',   body: 'Clinic shadowing plus interviews with 18 patients from the failed pilot.' },
      { phase: 'Build',          weeks: 'Weeks 5–18',  body: 'Platform, apps and baseline engine developed against a 200-patient cohort.' },
      { phase: 'Clinical pilot', weeks: 'Weeks 19–24', body: 'Parallel run with the existing follow-up process, triage accuracy reviewed weekly.' },
      { phase: 'Scale',          weeks: 'Weeks 25–30', body: 'Full enrolment, EMR integration sign-off, support handover.' },
    ],
    quote: {
      text: 'It tells me who to worry about. The last system just told me everything, which is the same as telling me nothing.',
      author: 'Consultant Cardiologist',
      role: 'Client practice',
    },
  },

  // ──────────────────────────────────────────────── UTILITIES & ENERGY ──
  {
    slug: 'solar-plant-performance-intelligence',
    code: 'ZX-24-03',
    featured: true,
    title: 'Solar Plant Performance Intelligence',
    tagline: 'String-level truth across 48 MW',
    sector: 'utilities-energy',
    sectorName: 'Utilities & Energy',
    color: '#E67E22',
    year: '2024',
    duration: '8 months',
    team: '6 engineers',
    client: 'Independent power producer',
    clientNote: '48 MW across 6 sites',
    services: ['Edge Hardware', 'Firmware', 'Cloud Analytics', 'Dashboards'],
    image: '/visuals/sector-services/utilities-energy/4.png',
    gallery: [
      '/visuals/sector-services/utilities-energy/4.png',
      '/visuals/sector-services/utilities-energy/1.png',
      '/visuals/sector-services/utilities-energy/5.png',
    ],
    tags: ['Modbus', 'Edge Compute', 'PR Analytics', 'LTE'],
    summary:
      'String-level monitoring and performance-ratio analytics across a six-site portfolio, turning a monthly generation report into a daily list of exactly which strings are losing money.',
    challenge: {
      body:
        'The O&M team knew the portfolio underperformed its model by roughly 7%, but inverter-level data could not say where. Finding a failed string meant walking rows with a clamp meter.',
      points: [
        'Inverter-level granularity only — a dead string vanished inside its array average',
        'Six sites, three inverter brands, two SCADA vendors, no common data model',
        'Remote sites with intermittent LTE and no reliable site power for monitoring gear',
        'Soiling and shading losses indistinguishable from real hardware faults',
      ],
    },
    approach: {
      body:
        'We instrumented at string level and pushed the analytics to the edge, so a site with a dead LTE link still knows it has a problem and still logs it.',
      steps: [
        { title: 'String sensing', body: 'Combiner-box current sensing per string, self-powered from the string itself, installed without taking arrays offline.' },
        { title: 'Edge PR engine', body: 'Each site computes its own performance ratio against local irradiance, so analysis survives connectivity loss.' },
        { title: 'Loss attribution', body: 'Soiling, shading, temperature and hardware faults separated by comparing neighbouring strings under identical irradiance.' },
        { title: 'Work-order output', body: 'The dashboard emits ranked field tasks with row and string references — not charts for the engineer to interpret.' },
      ],
    },
    outcome: {
      body:
        'Underperformance became a located, costed, dispatchable task. The team stopped walking rows and started fixing the strings the system named.',
      points: [
        'Mean time to locate a faulty string fell from days to under an hour',
        'Cleaning moved to soiling-triggered scheduling instead of fixed intervals',
        'Investor generation reports produced automatically from the same data',
      ],
    },
    metrics: [
      { value: '+6.4%',  label: 'Yield Recovered',    sub: 'portfolio, year 1' },
      { value: '48MW',   label: 'Under Monitoring',   sub: '6 sites' },
      { value: '<1h',    label: 'Fault Localisation', sub: 'was 2–3 days' },
      { value: '11,200', label: 'Strings Monitored',  sub: 'individually' },
    ],
    stack: [
      { group: 'Edge',      items: ['C++', 'Linux', 'Modbus TCP/RTU', 'SunSpec', 'LTE-M'] },
      { group: 'Platform',  items: ['Go', 'TimescaleDB', 'Grafana', 'Kubernetes'] },
      { group: 'Analytics', items: ['Python', 'PR modelling', 'Soiling attribution'] },
    ],
    architecture: [
      { layer: 'EDGE',     title: 'Array sensing', nodes: ['String CTs', 'Irradiance sensor', 'Module temp'] },
      { layer: 'GATEWAY',  title: 'Site compute',  nodes: ['Inverter polling', 'Local PR engine', 'Buffered upload'] },
      { layer: 'PLATFORM', title: 'Portfolio',     nodes: ['Time-series store', 'Loss attribution', 'Alert rules'] },
      { layer: 'SURFACE',  title: 'Operations',    nodes: ['Field work orders', 'Portfolio view', 'Investor reports'] },
    ],
    timeline: [
      { phase: 'Site survey', weeks: 'Weeks 1–5',   body: 'Six-site audit, protocol inventory, sensor placement plan per array.' },
      { phase: 'Pilot site',  weeks: 'Weeks 6–14',  body: 'One 6MW site fully instrumented and validated against manual IV-curve tracing.' },
      { phase: 'Rollout',     weeks: 'Weeks 15–28', body: 'Remaining five sites, staged to avoid generation downtime.' },
      { phase: 'Analytics',   weeks: 'Weeks 29–34', body: 'Attribution model tuned on a full seasonal cycle, reporting automated.' },
    ],
    quote: {
      text: 'We used to argue about why the plant missed model. Now the system tells us the row number.',
      author: 'Head of O&M',
      role: 'Client IPP',
    },
  },

  {
    slug: 'dg-fleet-automation',
    code: 'ZX-23-02',
    featured: false,
    title: 'DG Fleet Automation',
    tagline: '190 generators, one control room',
    sector: 'utilities-energy',
    sectorName: 'Utilities & Energy',
    color: '#E67E22',
    year: '2023',
    duration: '6 months',
    team: '5 engineers',
    client: 'Telecom infrastructure operator',
    clientNote: '190 remote tower sites',
    services: ['Retrofit Hardware', 'Firmware', 'Remote Control', 'Fuel Analytics'],
    image: '/visuals/sector-services/utilities-energy/3.png',
    gallery: ['/visuals/sector-services/utilities-energy/3.png', '/visuals/sector-services/utilities-energy/2.png'],
    tags: ['AMF', 'CAN', 'Fuel Sensing', 'LTE'],
    summary:
      'A retrofit controller and remote command layer for a dispersed diesel generator fleet, replacing site visits with verified remote start, fuel truth and condition-based servicing.',
    challenge: {
      body:
        'Fuel loss and unexplained generator failures were both suspected and unprovable. Every diagnosis needed a technician to drive to a tower, often to find nothing wrong.',
      points: [
        'Fuel reconciliation relied on delivery dockets and a dipstick',
        'Nine generator makes across the fleet, four with no digital interface at all',
        'Sites with poor LTE where a controller could not assume connectivity',
        'A failed remote start had to be provably safe — no unattended fire risk',
      ],
    },
    approach: {
      body:
        'The controller is autonomous first and connected second: it runs correct AMF logic on-site whether or not the network exists, and reports what it did afterwards.',
      steps: [
        { title: 'Universal retrofit', body: 'One controller with configurable I/O and CAN, mapped per generator make through a profile file rather than a hardware variant.' },
        { title: 'Fuel truth', body: 'Capacitive level sensing plus run-hour cross-checks flags theft-shaped drops distinct from consumption-shaped ones.' },
        { title: 'Safe remote start', body: 'Interlocked start sequence with local abort authority — the site always wins over the control room.' },
        { title: 'Condition-based service', body: 'Servicing scheduled on load-weighted run hours and start-attempt health, not calendar months.' },
      ],
    },
    outcome: {
      body:
        'Truck rolls dropped sharply, and fuel variance became a measured number the operator could act on rather than a suspicion.',
      points: [
        'Unexplained fuel variance reduced to a reconcilable figure per site',
        'Start failures detected during weekly automated test runs, not during outages',
        'Field visits now dispatched with a known fault, not to investigate',
      ],
    },
    metrics: [
      { value: '190',   label: 'Sites Automated', sub: 'single fleet view' },
      { value: '−64%',  label: 'Truck Rolls',     sub: 'year over year' },
      { value: '18%',   label: 'Fuel Cost Cut',   sub: 'variance recovered' },
      { value: '99.4%', label: 'Start Success',   sub: 'on demand' },
    ],
    stack: [
      { group: 'Edge',     items: ['C', 'STM32F4', 'CAN J1939', 'Modbus', 'LTE Cat-M'] },
      { group: 'Platform', items: ['Node.js', 'MQTT', 'PostgreSQL', 'Redis'] },
      { group: 'Surface',  items: ['React', 'Mapbox GL', 'Role-based control'] },
    ],
    architecture: [
      { layer: 'EDGE',     title: 'Generator site',   nodes: ['AMF controller', 'Fuel sensor', 'Engine CAN', 'ATS interlock'] },
      { layer: 'GATEWAY',  title: 'On-site autonomy', nodes: ['Local logic', 'Event log', 'Buffered telemetry'] },
      { layer: 'PLATFORM', title: 'Fleet backend',    nodes: ['Command queue', 'Fuel reconciliation', 'Service engine'] },
      { layer: 'SURFACE',  title: 'Control room',     nodes: ['Fleet map', 'Remote start/stop', 'Fuel reports'] },
    ],
    timeline: [
      { phase: 'Fleet audit',   weeks: 'Weeks 1–3',   body: 'Nine generator makes profiled, interface matrix and safety analysis produced.' },
      { phase: 'Controller',    weeks: 'Weeks 4–12',  body: 'Board design, AMF logic, safety interlock review and bench validation.' },
      { phase: 'Pilot',         weeks: 'Weeks 13–18', body: '20 sites live across the worst-connectivity region in the fleet.' },
      { phase: 'Fleet rollout', weeks: 'Weeks 19–26', body: 'Remaining 170 sites, installer training, control-room handover.' },
    ],
    quote: null,
  },

  {
    slug: 'battery-bank-health-monitoring',
    code: 'ZX-22-09',
    featured: false,
    title: 'Battery Bank Health Monitoring',
    tagline: 'Knowing which cell fails, before it does',
    sector: 'utilities-energy',
    sectorName: 'Utilities & Energy',
    color: '#E67E22',
    year: '2022',
    duration: '4 months',
    team: '4 engineers',
    client: 'Data centre operator',
    clientNote: '1,150 cells under monitoring',
    services: ['Sensing Hardware', 'Firmware', 'Predictive Analytics'],
    image: '/visuals/sector-services/utilities-energy/2.png',
    gallery: ['/visuals/sector-services/utilities-energy/2.png', '/visuals/sector-services/utilities-energy/5.png'],
    tags: ['Cell-level', 'Thermal', 'SoH', 'Modbus'],
    summary:
      'Cell-level voltage, impedance and thermal monitoring across a UPS battery estate, replacing quarterly manual load tests with continuous state-of-health trending.',
    challenge: {
      body:
        'Battery banks were tested quarterly by discharging them — a test that ages the very asset it measures, and that still missed cells failing in between.',
      points: [
        'Quarterly load testing consumed cycle life on every healthy string',
        'A single weak cell could take an entire string down without warning',
        'Thermal runaway risk with no early indicator in place',
      ],
    },
    approach: {
      body:
        'Continuous per-cell measurement replaces the destructive test. Impedance trending identifies the weak cell while it is still merely weak.',
      steps: [
        { title: 'Per-cell sensing', body: 'Isolated voltage and impedance measurement per cell with daisy-chained modules and a single string controller.' },
        { title: 'Thermal watch', body: 'Distributed temperature sensing with rate-of-rise detection rather than absolute thresholds alone.' },
        { title: 'SoH trending', body: 'Impedance drift tracked per cell against its own history, flagging outliers within the string.' },
        { title: 'Replacement planning', body: 'Weak-cell forecasts feed a quarterly replacement plan instead of emergency swaps.' },
      ],
    },
    outcome: {
      body:
        'Destructive testing was retired, and battery replacement became a planned line item rather than an incident response.',
      points: [
        'Quarterly discharge testing eliminated across the estate',
        'Weak cells identified an average of 11 weeks before string impact',
        'Thermal excursions caught at rate-of-rise, before absolute limits',
      ],
    },
    metrics: [
      { value: '1,150', label: 'Cells Monitored',   sub: 'continuous' },
      { value: '11wk',  label: 'Early Warning',     sub: 'before string impact' },
      { value: '0',     label: 'Unplanned Outages', sub: 'since deployment' },
      { value: '−37%',  label: 'Battery Spend',     sub: 'planned replacement' },
    ],
    stack: [
      { group: 'Edge',      items: ['C', 'MSP430', 'Isolated ADC', 'Modbus RTU'] },
      { group: 'Platform',  items: ['Python', 'InfluxDB', 'Grafana'] },
      { group: 'Analytics', items: ['Impedance trending', 'Outlier detection'] },
    ],
    architecture: [
      { layer: 'EDGE',     title: 'Cell modules',      nodes: ['Voltage sense', 'Impedance', 'Cell temperature'] },
      { layer: 'GATEWAY',  title: 'String controller', nodes: ['Daisy-chain bus', 'Rate-of-rise logic'] },
      { layer: 'PLATFORM', title: 'Analytics',         nodes: ['SoH trending', 'Outlier ranking'] },
      { layer: 'SURFACE',  title: 'Facilities',        nodes: ['String health view', 'Replacement plan'] },
    ],
    timeline: [
      { phase: 'Design', weeks: 'Weeks 1–4',   body: 'Isolation topology and measurement accuracy validated on a test string.' },
      { phase: 'Build',  weeks: 'Weeks 5–10',  body: 'Module production run, controller firmware, safety review.' },
      { phase: 'Deploy', weeks: 'Weeks 11–16', body: 'Estate-wide installation during scheduled maintenance windows.' },
    ],
    quote: null,
  },

  // ────────────────────────────────────────────────────── INDUSTRIAL IoT ──
  {
    slug: 'smart-factory-oee-platform',
    code: 'ZX-23-11',
    featured: true,
    title: 'Smart Factory OEE Platform',
    tagline: 'Downtime with a reason attached',
    sector: 'industrial-iot',
    sectorName: 'Industrial IoT',
    color: '#5D4037',
    year: '2023',
    duration: '10 months',
    team: '8 engineers',
    client: 'Automotive component manufacturer',
    clientNote: '3 plants · 142 machines',
    services: ['Edge Gateways', 'PLC Integration', 'Analytics', 'MES Integration'],
    image: '/visuals/sector-services/industrial-iot/1.png',
    gallery: [
      '/visuals/sector-services/industrial-iot/1.png',
      '/visuals/sector-services/industrial-iot/3.png',
      '/visuals/sector-services/industrial-iot/5.png',
    ],
    tags: ['OPC-UA', 'Modbus', 'Andon', 'Kafka'],
    summary:
      'Machine-level OEE across three plants with automatic downtime classification, replacing shift-end clipboards with live production truth the floor actually trusts.',
    challenge: {
      body:
        'OEE was reconstructed at shift end from operator notes. The numbers arrived a day late, disagreed with the ERP, and nobody on the floor believed them — so nobody acted on them.',
      points: [
        'Machines spanning four decades: modern OPC-UA alongside relay-logic presses',
        'Downtime reasons written by hand, inconsistently, hours after the event',
        'Production counts disagreed with ERP by 3–7% every single week',
        'Any solution had to work without adding operator data-entry burden',
      ],
    },
    approach: {
      body:
        'We captured signals wherever they existed and inferred the rest — then made operator confirmation a two-tap Andon interaction instead of a paper form.',
      steps: [
        { title: 'Mixed-age capture', body: 'OPC-UA and Modbus where available; current-clamp and vibration signature inference on machines with no digital output.' },
        { title: 'Automatic classification', body: 'Stoppage patterns classified into causes automatically, with the operator confirming or correcting in two taps on the Andon screen.' },
        { title: 'Count reconciliation', body: 'Machine counts reconciled to ERP nightly with a variance report naming the specific machine and shift.' },
        { title: 'Floor-first surfaces', body: 'Line-side Andon displays designed for glance-reading at 5 metres, in plant lighting, with gloves on.' },
      ],
    },
    outcome: {
      body:
        'OEE became a live number the floor argued with rather than ignored — and the arguments produced fixes.',
      points: [
        'Downtime reasons captured within seconds of the stoppage, not hours after',
        'ERP count variance closed to under 0.4% and now auditable per machine',
        'Same platform extended to two further plants without new engineering',
      ],
    },
    metrics: [
      { value: '+19pts', label: 'OEE Improvement',    sub: '61% → 80%' },
      { value: '142',    label: 'Machines Connected', sub: '3 plants' },
      { value: '0.4%',   label: 'ERP Count Variance', sub: 'from 3–7%' },
      { value: '−41%',   label: 'Unplanned Downtime', sub: 'year over year' },
    ],
    stack: [
      { group: 'Edge',     items: ['C++', 'Linux', 'OPC-UA', 'Modbus TCP', 'Signature inference'] },
      { group: 'Platform', items: ['Go', 'Kafka', 'TimescaleDB', 'Kubernetes'] },
      { group: 'Surface',  items: ['React', 'Andon displays', 'MES / SAP APIs'] },
    ],
    architecture: [
      { layer: 'EDGE',     title: 'Machine capture', nodes: ['PLC tags', 'Current clamps', 'Vibration', 'Counters'] },
      { layer: 'GATEWAY',  title: 'Line gateway',    nodes: ['Protocol adapters', 'State machine', 'Buffering'] },
      { layer: 'PLATFORM', title: 'Stream & model',  nodes: ['Event stream', 'OEE engine', 'Reason classifier'] },
      { layer: 'SURFACE',  title: 'Plant & ERP',     nodes: ['Andon boards', 'Shift reports', 'MES sync'] },
    ],
    timeline: [
      { phase: 'Line study',  weeks: 'Weeks 1–6',   body: 'Every machine profiled for available signals; inference plan for the silent ones.' },
      { phase: 'Pilot line',  weeks: 'Weeks 7–16',  body: 'One line instrumented end-to-end, run parallel to clipboards for verification.' },
      { phase: 'Plant one',   weeks: 'Weeks 17–30', body: 'Full first plant, Andon rollout, operator training on the two-tap flow.' },
      { phase: 'Multi-plant', weeks: 'Weeks 31–42', body: 'Plants two and three, ERP reconciliation, central reporting handover.' },
    ],
    quote: {
      text: 'The board on the line is the first version of these numbers that the shop floor has ever trusted.',
      author: 'Plant Operations Manager',
      role: 'Client manufacturer',
    },
  },

  {
    slug: 'predictive-maintenance-rotating-assets',
    code: 'ZX-24-08',
    featured: false,
    title: 'Predictive Maintenance for Rotating Assets',
    tagline: 'Bearings that ask for help early',
    sector: 'industrial-iot',
    sectorName: 'Industrial IoT',
    color: '#5D4037',
    year: '2024',
    duration: '7 months',
    team: '5 engineers',
    client: 'Process manufacturing group',
    clientNote: '310 critical assets',
    services: ['Wireless Sensing', 'Edge ML', 'CMMS Integration'],
    image: '/visuals/sector-services/industrial-iot/2.png',
    gallery: ['/visuals/sector-services/industrial-iot/2.png', '/visuals/sector-services/industrial-iot/4.png'],
    tags: ['Vibration', 'Edge ML', 'Wireless', 'CMMS'],
    summary:
      'Wireless vibration and thermal sensing on pumps, fans and gearboxes, with on-sensor feature extraction that turns raw spectra into named, actionable failure modes.',
    challenge: {
      body:
        'Condition monitoring existed as a quarterly route with a handheld analyser. Between routes, machines failed silently — and the specialist who could read the spectra was one person nearing retirement.',
      points: [
        'Quarterly manual routes left a 90-day blind window per asset',
        'Spectral interpretation depended on a single retiring specialist',
        'No cabling budget or downtime window for wired sensors',
        'Alerts had to name the failure mode, not just say "anomaly"',
      ],
    },
    approach: {
      body:
        'Battery-powered sensors extract features on-device so the radio carries kilobytes, not megabytes — and the model outputs a named fault, not a score.',
      steps: [
        { title: 'On-sensor features', body: 'FFT, envelope and bearing-frequency features computed on the sensor so battery life reaches five years.' },
        { title: 'Named fault modes', body: 'Classifiers trained per asset class output bearing wear, misalignment, imbalance or looseness — with confidence.' },
        { title: 'Specialist capture', body: 'The retiring analyst’s labelling of three years of archived spectra became the training and validation set.' },
        { title: 'Work-order loop', body: 'Confirmed alerts open a CMMS work order automatically with the recommended action pre-filled.' },
      ],
    },
    outcome: {
      body:
        'The 90-day blind window closed to continuous coverage, and the diagnostic knowledge that was going to walk out the door stayed in the system.',
      points: [
        'Continuous coverage on 310 assets with no cabling and no downtime to install',
        'Lead time on bearing faults averaged 6 weeks before functional failure',
        'Maintenance planning shifted from calendar routes to condition-driven work orders',
      ],
    },
    metrics: [
      { value: '310',  label: 'Assets Covered',  sub: 'wireless sensors' },
      { value: '6wk',  label: 'Fault Lead Time', sub: 'before failure' },
      { value: '5yr',  label: 'Sensor Battery',  sub: 'edge feature extraction' },
      { value: '−58%', label: 'Emergency Work',  sub: 'vs planned' },
    ],
    stack: [
      { group: 'Edge',     items: ['C', 'Cortex-M4F', 'MEMS accelerometer', 'Sub-GHz mesh'] },
      { group: 'Platform', items: ['Python', 'FastAPI', 'TimescaleDB', 'Docker'] },
      { group: 'ML',       items: ['scikit-learn', 'Envelope analysis', 'Per-class models'] },
    ],
    architecture: [
      { layer: 'EDGE',     title: 'Wireless sensors', nodes: ['Triaxial accel', 'Surface temp', 'On-device FFT'] },
      { layer: 'GATEWAY',  title: 'Plant mesh',       nodes: ['Sub-GHz mesh', 'Feature relay'] },
      { layer: 'PLATFORM', title: 'Diagnosis',        nodes: ['Fault classifiers', 'Confidence scoring', 'History'] },
      { layer: 'SURFACE',  title: 'Maintenance',      nodes: ['Named alerts', 'CMMS work orders', 'Asset health'] },
    ],
    timeline: [
      { phase: 'Data mining',  weeks: 'Weeks 1–5',   body: 'Three years of archived spectra labelled with the in-house specialist.' },
      { phase: 'Sensor build', weeks: 'Weeks 6–14',  body: 'Sensor hardware, on-device feature pipeline, five-year power budget validation.' },
      { phase: 'Pilot',        weeks: 'Weeks 15–22', body: '40 assets monitored in parallel with the manual route for cross-validation.' },
      { phase: 'Rollout',      weeks: 'Weeks 23–30', body: 'Full 310-asset deployment and CMMS integration.' },
    ],
    quote: null,
  },

  {
    slug: 'plant-utility-metering-network',
    code: 'ZX-22-04',
    featured: false,
    title: 'Plant Utility Metering Network',
    tagline: 'The true cost of a compressed-air leak',
    sector: 'industrial-iot',
    sectorName: 'Industrial IoT',
    color: '#5D4037',
    year: '2022',
    duration: '5 months',
    team: '4 engineers',
    client: 'Food processing plant',
    clientNote: '1 site · 78 metering points',
    services: ['Metering Hardware', 'Data Acquisition', 'Cost Analytics'],
    image: '/visuals/sector-services/industrial-iot/5.png',
    gallery: ['/visuals/sector-services/industrial-iot/5.png', '/visuals/sector-services/industrial-iot/3.png'],
    tags: ['Flow', 'Sub-metering', 'Cost/Unit', 'Modbus'],
    summary:
      'Sub-metering of compressed air, water, steam and power correlated against production output, exposing utility cost per unit produced for the first time.',
    challenge: {
      body:
        'The plant had one electricity meter, one water meter and a compressed-air bill nobody could attribute. Utility cost per product was a spreadsheet guess divided by tonnage.',
      points: [
        'No sub-metering — utilities allocated by floor area, not consumption',
        'Compressed air leaks audible only during the annual shutdown survey',
        'Utility use never correlated with production line or product mix',
      ],
    },
    approach: {
      body:
        'Meter the branches that matter, then join the meter data to production records so every line carries its own real utility cost.',
      steps: [
        { title: 'Branch metering', body: '78 metering points across air, water, steam and power selected by spend contribution rather than convenience.' },
        { title: 'Idle-period baseline', body: 'Consumption during planned no-production windows establishes the leak and standby floor per branch.' },
        { title: 'Cost joining', body: 'Meter streams joined to production records to produce utility cost per unit, per line, per shift.' },
        { title: 'Leak alerting', body: 'A rising idle baseline triggers a leak alert naming the branch, long before the annual survey.' },
      ],
    },
    outcome: {
      body:
        'Utility cost stopped being overhead and became a per-line number that operations could see move week to week.',
      points: [
        'Compressed air leak load identified and reduced within the first quarter',
        'Utility cost per unit reported per line and product for the first time',
        'Standby consumption during idle shifts cut by shutdown sequencing',
      ],
    },
    metrics: [
      { value: '78',   label: 'Metering Points',  sub: 'air, water, steam, power' },
      { value: '−22%', label: 'Air Leak Load',    sub: 'first quarter' },
      { value: '−14%', label: 'Utility Cost/Unit',sub: 'year one' },
      { value: '7mo',  label: 'Payback',          sub: 'on full deployment' },
    ],
    stack: [
      { group: 'Edge',     items: ['Modbus RTU', 'Pulse counting', 'Ultrasonic flow'] },
      { group: 'Platform', items: ['Python', 'PostgreSQL', 'Airflow'] },
      { group: 'Surface',  items: ['React', 'Cost dashboards'] },
    ],
    architecture: [
      { layer: 'EDGE',     title: 'Meters',        nodes: ['Air flow', 'Water flow', 'Steam', 'Power'] },
      { layer: 'GATEWAY',  title: 'Acquisition',   nodes: ['Modbus poller', 'Pulse capture', 'Local buffer'] },
      { layer: 'PLATFORM', title: 'Correlation',   nodes: ['Production join', 'Baseline engine', 'Leak detection'] },
      { layer: 'SURFACE',  title: 'Finance & ops', nodes: ['Cost per unit', 'Line comparison', 'Leak alerts'] },
    ],
    timeline: [
      { phase: 'Survey',    weeks: 'Weeks 1–3',   body: 'Utility distribution mapped, metering points ranked by spend contribution.' },
      { phase: 'Install',   weeks: 'Weeks 4–12',  body: 'Meter installation staged around production, mostly hot-tap and clamp-on.' },
      { phase: 'Analytics', weeks: 'Weeks 13–20', body: 'Production join, baselines established across a full product-mix cycle.' },
    ],
    quote: null,
  },

  // ───────────────────────────────────────────────────────── EDUCATION ──
  {
    slug: 'campus-robotics-lab-programme',
    code: 'ZX-24-06',
    featured: false,
    title: 'Campus Robotics Lab Programme',
    tagline: 'Empty room to competition team in one semester',
    sector: 'education',
    sectorName: 'Education',
    color: '#1565A7',
    year: '2024',
    duration: '6 months',
    team: '5 engineers + 2 trainers',
    client: 'Engineering college network',
    clientNote: '4 campuses · 1,800 students',
    services: ['Lab Design', 'Hardware Kits', 'Curriculum', 'Faculty Training'],
    image: '/visuals/sector-services/education/2.png',
    gallery: [
      '/visuals/sector-services/education/2.png',
      '/visuals/sector-services/education/1.png',
      '/visuals/sector-services/education/3.png',
    ],
    tags: ['Robotics', 'ROS 2', 'Curriculum', 'Training'],
    summary:
      'Four turnkey robotics and automation labs delivered with hardware, curriculum and faculty enablement — designed so the labs keep running after the vendor leaves.',
    challenge: {
      body:
        'The network had bought robotics kits twice before. Both sets sat in cupboards because no faculty member felt able to teach with them and no curriculum referenced them.',
      points: [
        'Prior kit purchases unused — hardware without teaching support',
        'Faculty confidence, not equipment, was the actual constraint',
        'Four campuses with different room sizes, budgets and student mixes',
        'Lab content had to map to AICTE and NBA outcome requirements',
      ],
    },
    approach: {
      body:
        'We treated faculty capability as the deliverable and the hardware as its support. Nothing shipped until the person teaching it had built with it.',
      steps: [
        { title: 'Outcome mapping', body: 'Every lab exercise mapped to a specific AICTE/NBA course outcome before any hardware was specified.' },
        { title: 'Tiered kits', body: 'A common platform with three capability tiers so all four campuses share curriculum despite different budgets.' },
        { title: 'Build-it-first training', body: 'Faculty completed the full student project themselves across a two-week intensive before term one.' },
        { title: 'Competition on-ramp', body: 'A structured path from coursework to a competition-ready team, with mentor sessions through the first season.' },
      ],
    },
    outcome: {
      body:
        'All four labs were in weekly timetabled use through the following academic year — the first time equipment at this network outlived its purchase cycle.',
      points: [
        'Weekly timetabled use across all four campuses, not occasional demos',
        'Faculty delivering the curriculum unaided from term two onward',
        'Two campuses entered national-level competitions in the first season',
      ],
    },
    metrics: [
      { value: '4',     label: 'Labs Delivered',     sub: 'turnkey' },
      { value: '1,800', label: 'Students Reached',   sub: 'first year' },
      { value: '32',    label: 'Faculty Trained',    sub: 'build-it-first' },
      { value: '100%',  label: 'Labs In Weekly Use', sub: 'year one' },
    ],
    stack: [
      { group: 'Hardware',   items: ['ARM controllers', 'Sensor suites', 'Actuator kits', 'Bench power'] },
      { group: 'Software',   items: ['Python', 'C++', 'ROS 2', 'Block coding'] },
      { group: 'Curriculum', items: ['AICTE mapping', 'NBA outcomes', 'Lab manuals'] },
    ],
    architecture: [
      { layer: 'FOUNDATION', title: 'Lab platform', nodes: ['Controllers', 'Sensor kits', 'Actuators', 'Safety'] },
      { layer: 'TOOLING',    title: 'Environments', nodes: ['Python / C++', 'ROS 2', 'Block coding'] },
      { layer: 'CURRICULUM', title: 'Teaching',     nodes: ['Mapped exercises', 'Lab manuals', 'Assessments'] },
      { layer: 'CAPABILITY', title: 'People',       nodes: ['Faculty intensive', 'Mentor sessions', 'Competition path'] },
    ],
    timeline: [
      { phase: 'Design',     weeks: 'Weeks 1–5',   body: 'Outcome mapping with faculty and industry advisors; per-campus room plans.' },
      { phase: 'Build',      weeks: 'Weeks 6–14',  body: 'Kit assembly, lab fit-out, manual authoring and internal dry-runs.' },
      { phase: 'Enablement', weeks: 'Weeks 15–18', body: 'Two-week faculty intensive per campus, full student project completed.' },
      { phase: 'First term', weeks: 'Weeks 19–26', body: 'Supported teaching through term one, then unaided handover.' },
    ],
    quote: {
      text: 'The difference this time was that we built the project before the students did. That is why the room is still in use.',
      author: 'Head of Department',
      role: 'Client college network',
    },
  },

  {
    slug: 'cloud-learning-platform',
    code: 'ZX-23-05',
    featured: false,
    title: 'Cloud Learning Platform',
    tagline: 'A campus that survives the timetable',
    sector: 'education',
    sectorName: 'Education',
    color: '#1565A7',
    year: '2023',
    duration: '8 months',
    team: '6 engineers',
    client: 'Private education group',
    clientNote: '11,000 learners',
    services: ['Platform Engineering', 'Adaptive Learning', 'Analytics'],
    image: '/visuals/sector-services/education/4.png',
    gallery: ['/visuals/sector-services/education/4.png', '/visuals/sector-services/education/3.png'],
    tags: ['LMS', 'Adaptive', 'Analytics', 'Kubernetes'],
    summary:
      'A white-label learning platform with adaptive pathways and low-bandwidth delivery, built for learners whose connection is a shared phone on patchy mobile data.',
    challenge: {
      body:
        'The group’s existing LMS assumed a laptop and broadband. Most of its learners had neither, so completion rates collapsed outside the main campus.',
      points: [
        'Video-first content unusable on the bandwidth most learners actually had',
        'No visibility into where in a module learners were dropping out',
        'Assessment integrity concerns blocking any remote examination',
      ],
    },
    approach: {
      body:
        'We designed for the worst connection in the cohort and let better connections benefit, rather than the other way round.',
      steps: [
        { title: 'Low-bandwidth first', body: 'Adaptive bitrate with an audio-plus-slides fallback and full offline module download over any available Wi-Fi.' },
        { title: 'Dropout analytics', body: 'Per-section engagement telemetry shows exactly where in a module learners stop, feeding content revision.' },
        { title: 'Adaptive pathways', body: 'Assessment results reroute learners to prerequisite content automatically instead of failing them forward.' },
        { title: 'Proctoring that fits', body: 'Lightweight integrity checks that work on a low-end phone camera without continuous video upload.' },
      ],
    },
    outcome: {
      body:
        'Completion outside the main campus rose to within a few points of on-campus rates, and content revision became evidence-driven.',
      points: [
        'Off-campus completion within 5 points of on-campus for the first time',
        'Module content revised against measured dropout points, not intuition',
        'Remote assessment accepted by the group’s academic board',
      ],
    },
    metrics: [
      { value: '11,000', label: 'Active Learners',  sub: 'across the group' },
      { value: '+38%',   label: 'Completion Rate',  sub: 'off-campus cohort' },
      { value: '−86%',   label: 'Data Per Module',  sub: 'low-bandwidth mode' },
      { value: '99.95%', label: 'Platform Uptime',  sub: 'exam periods included' },
    ],
    stack: [
      { group: 'Platform',  items: ['Node.js', 'PostgreSQL', 'Redis', 'Kubernetes', 'S3'] },
      { group: 'Delivery',  items: ['Adaptive bitrate', 'Offline sync', 'PWA'] },
      { group: 'Analytics', items: ['Engagement telemetry', 'Cohort analysis'] },
    ],
    architecture: [
      { layer: 'CONTENT',  title: 'Authoring', nodes: ['Module builder', 'Assessment bank', 'Media pipeline'] },
      { layer: 'DELIVERY', title: 'Reach',     nodes: ['Adaptive streams', 'Offline packages', 'PWA client'] },
      { layer: 'PLATFORM', title: 'Core',      nodes: ['Enrolment', 'Adaptive engine', 'Proctoring'] },
      { layer: 'INSIGHT',  title: 'Analytics', nodes: ['Dropout mapping', 'Cohort dashboards', 'Outcome reports'] },
    ],
    timeline: [
      { phase: 'Research',   weeks: 'Weeks 1–4',   body: 'Connectivity and device survey across the actual learner population.' },
      { phase: 'Core build', weeks: 'Weeks 5–20',  body: 'Platform, adaptive engine and low-bandwidth delivery pipeline.' },
      { phase: 'Migration',  weeks: 'Weeks 21–28', body: 'Content migration from the legacy LMS, staff training, phased cohort cutover.' },
      { phase: 'Exams',      weeks: 'Weeks 29–34', body: 'Proctoring validation and the first full remote assessment cycle.' },
    ],
    quote: null,
  },
];

/* ── Aggregate figures shown in the showcase hero + impact band ── */
export const PROJECT_IMPACT = [
  { target: 120, suffix: '+',   label: 'Products Shipped',  sub: 'since 2017' },
  { target: 45,  suffix: '',    label: 'Engineers',         sub: 'in-house team' },
  { target: 12,  suffix: '',    label: 'Industries Served', sub: 'and counting' },
  { target: 99,  suffix: '.9%', label: 'Fleet Uptime',      sub: 'deployed systems' },
];

/* ── Technologies surfaced as a marquee on the showcase page ── */
export const PROJECT_TECH = [
  'C / C++', 'Rust', 'Python', 'Go', 'FreeRTOS', 'Zephyr', 'Linux',
  'ARM Cortex-M', 'ESP32', 'STM32', 'BLE 5', 'LoRaWAN', 'LTE Cat-M', 'Modbus',
  'OPC-UA', 'CAN', 'MQTT', 'Kafka', 'TimescaleDB', 'PostgreSQL', 'InfluxDB',
  'AWS IoT', 'Kubernetes', 'Docker', 'React', 'React Native', 'TensorFlow Lite',
  'HL7 FHIR', 'Grafana', 'Edge ML',
];

export function findProject(slug) {
  return PROJECTS.find(p => p.slug === slug) || null;
}

export function adjacentProjects(slug) {
  const i = PROJECTS.findIndex(p => p.slug === slug);
  if (i === -1) return { prev: null, next: null };
  return {
    prev: PROJECTS[(i - 1 + PROJECTS.length) % PROJECTS.length],
    next: PROJECTS[(i + 1) % PROJECTS.length],
  };
}

export function projectsBySector(sectorSlug) {
  return sectorSlug === 'all' ? PROJECTS : PROJECTS.filter(p => p.sector === sectorSlug);
}
